// ============================================
// Reports Hooks - TanStack Query
// ============================================

import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import type { UUID } from '@conai/shared-types';
import { useApi, useReportsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

type DashboardPeriod = 'daily' | 'weekly' | 'monthly';

export interface DashboardOverviewResponse {
  today?: {
    orders?: number;
    revenue?: string;
  };
  this_week?: {
    orders?: number;
    revenue?: string;
  };
  this_month?: {
    orders?: number;
    revenue?: string;
  };
  alerts?: {
    low_stock_count?: number;
  };
  recent_orders?: Array<{
    order_code?: string;
    total?: string;
    status?: string;
    created_at?: string;
  }>;
  top_products?: Array<{
    product_name?: string;
    quantity?: number;
    revenue?: string;
  }>;
}

export interface RevenueTrendParams {
  start_date: string;
  end_date: string;
  period?: DashboardPeriod;
  store_id?: UUID;
  /**
   * Danh sách order_status phân tách bằng dấu phẩy. Bỏ trống thì BE chỉ đếm
   * COMPLETED + PAID — đơn vừa tạo sẽ không vào tổng.
   */
  order_status?: string;
}

export interface RevenueTrendResponse {
  period: DashboardPeriod;
  data: Array<{
    date: string;
    revenue: string;
    order_count: number;
    discount_total?: string;
    tax_total?: string;
    cost?: string;
    profit?: string;
  }>;
  totals: {
    revenue: string;
    order_count: number;
    discount_total?: string;
    tax_total?: string;
    cost?: string;
    profit?: string;
  };
  /** Doanh thu/giá vốn/lợi nhuận theo từng store (BE sắp theo profit giảm dần). */
  by_store?: Array<{
    store_id: string;
    store_name: string;
    revenue: string;
    cost: string;
    profit: string;
    order_count: number;
  }>;

  // ---- Đa tiền tệ (sheet 2 mục #75) ----
  //
  // `totals` ở trên CỘNG GỘP mọi cửa hàng trong phạm vi, bất kể tiền tệ. Với
  // chuỗi nhiều thị trường thì con số đó vô nghĩa nhưng trông hoàn toàn bình
  // thường — đọc `totals_by_currency` thay thế khi `mixed_currencies` bật.

  /** BE bật khi phạm vi đang xem gồm từ hai loại tiền trở lên. */
  mixed_currencies?: boolean;
  /** Tiền tệ duy nhất của phạm vi — chỉ có nghĩa khi `mixed_currencies` false. */
  currency?: string | null;
  /** Các mã tiền tệ xuất hiện trong phạm vi. */
  currencies?: string[];
  /**
   * Tổng TÁCH RIÊNG theo từng loại tiền (BE thêm 03/09/2026, cùng shape với
   * `/customers/profile/outstanding/`). Đây là con số dùng được khi trộn tiền tệ.
   */
  totals_by_currency?: Record<
    string,
    {
      revenue: string;
      order_count: number;
      discount_total?: string;
      tax_total?: string;
      cost?: string;
      profit?: string;
    }
  >;
}

/** Một dòng của `/payments/`. */
export interface RecentTransactionItem {
  id: string;
  order?: string;
  order_code?: string;
  provider?: string;
  provider_transaction_id?: string;
  transaction_id?: string;
  amount?: string | number;
  currency?: string;
  payment_method?: string;
  payment_status?: string;
  status?: string;
  requested_at?: string;
  confirmed_at?: string;
  failed_at?: string;
  created_at?: string;
  // Bug #27/#28: BE đã bổ sung khách hàng + sản phẩm vào serializer, FE không
  // phải gọi thêm /orders/ để ghép tay nữa.
  /** null khi khách vãng lai. */
  customer?: string | null;
  /** null khi khách vãng lai. */
  customer_name?: string | null;
  /** Mảng rỗng nếu payment không gắn với order nào. */
  order_items?: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    unit_price: string;
  }>;
  items_count?: number;
  order_total?: string;
  store_name?: string;
}

export interface PaginatedLike<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface OrderStatusTrendResponse {
  period: DashboardPeriod;
  data: Array<{
    date: string;
    status_counts: Record<string, number>;
  }>;
}

export interface TopProductsByCategoryResponse {
  period: DashboardPeriod;
  data: Array<{
    date: string;
    categories: Array<{
      category_id: string;
      category_name: string;
      products: Array<{
        product_id: string;
        product_name: string;
        quantity_sold: number;
        revenue: string;
      }>;
    }>;
  }>;
}

export function useReportsDashboard(storeId: UUID | undefined, options?: { enabled?: boolean }) {
  const reportsService = useReportsService();

  return useQuery({
    queryKey: queryKeys.reports.dashboard(storeId),
    queryFn: () => reportsService.getDashboardSummary(storeId!),
    enabled: (options?.enabled !== false) && !!storeId,
  });
}

export function useDailySalesReport(storeId: UUID | undefined, date?: string, options?: { enabled?: boolean }) {
  const reportsService = useReportsService();

  return useQuery({
    queryKey: queryKeys.reports.sales({ storeId, date }),
    queryFn: () => reportsService.getDailySales(storeId!, date),
    enabled: (options?.enabled !== false) && !!storeId,
  });
}

/**
 * Chốt cuối ngày (sheet 2 mục #53).
 *
 * `store_id` bắt buộc — báo cáo là "một cửa hàng một ngày", không có bản toàn
 * chuỗi; chưa chọn cửa hàng thì query tự tắt.
 *
 * `date` bỏ trống là hợp lệ: BE lấy hôm qua theo múi giờ CỦA CỬA HÀNG. Không tự
 * tính "hôm qua" ở client vì client không biết múi giờ đó.
 */
export function useDailyClose(
  params: { store_id?: UUID; date?: string },
  options?: { enabled?: boolean }
) {
  const reportsService = useReportsService();

  return useQuery({
    queryKey: queryKeys.reports.dailyClose(params),
    queryFn: () =>
      reportsService.getDailyClose({ store_id: params.store_id!, date: params.date }),
    enabled: options?.enabled !== false && Boolean(params.store_id),
    // Endpoint chưa deploy QA -> 404. Retry 3 lần chỉ làm màn hình treo ở
    // trạng thái loading lâu hơn trước khi hiện đúng lỗi.
    retry: false,
  });
}

export function useInventoryReport(storeId: UUID | undefined, options?: { enabled?: boolean }) {
  const reportsService = useReportsService();

  return useQuery({
    queryKey: queryKeys.reports.inventory({ storeId }),
    queryFn: () => reportsService.getInventoryReport(storeId!),
    enabled: (options?.enabled !== false) && !!storeId,
  });
}

export function useOrdersReport(
  storeId: UUID | undefined,
  params?: { start_date: string; end_date: string },
  options?: { enabled?: boolean }
) {
  const reportsService = useReportsService();

  return useQuery({
    queryKey: queryKeys.reports.orders({ storeId, ...params }),
    queryFn: () => reportsService.getOrdersReport(storeId!, params),
    enabled: (options?.enabled !== false) && !!storeId,
  });
}

export function useRevenueTrend(
  params: RevenueTrendParams,
  options?: { enabled?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.reports.revenueTrend(params as unknown as Record<string, unknown>),
    queryFn: () =>
      // Bỏ trống `period` để BE tự chọn bucket theo độ dài range
      // (<92d daily, <730d weekly, còn lại monthly). Ép 'daily' trên range dài
      // sẽ trả về hàng nghìn điểm.
      api.client.get<RevenueTrendResponse>('/reports/revenue/', {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.period ? { period: params.period } : {}),
        ...(params.order_status ? { order_status: params.order_status } : {}),
        store_id: params.store_id,
      }),
    enabled:
      options?.enabled !== false &&
      Boolean(params.start_date) &&
      Boolean(params.end_date),
  });
}

export function useRecentDashboardTransactions(
  params?: {
    page?: number;
    page_size?: number;
    ordering?: string;
    provider?: string;
    payment_status?: string;
    order?: string;
    store_id?: string;
    /** Cận dưới theo `requested_at`, dạng `YYYY-MM-DDTHH:mm:ss` (giờ server). */
    requested_at__gte?: string;
    /** Cận trên theo `requested_at`, dạng `YYYY-MM-DDTHH:mm:ss` (giờ server). */
    requested_at__lte?: string;
  },
  options?: { enabled?: boolean }
) {
  const api = useApi();

  // `latest_per_order=true`: BE gom mỗi đơn về một dòng (bản ghi thanh toán mới
  // nhất) TRƯỚC khi phân trang, nên `meta.count` là số ĐƠN và không đơn nào bị
  // sót vì rơi ngoài trang đang xem. Trước đây FE gom trên 50 bản ghi gần nhất,
  // đơn nào có quá nhiều lần thử nằm ngoài cửa sổ đó thì bị hiện lần thử cũ.
  //
  // Bật vô điều kiện: BE gom ở tầng ứng dụng nên chỉ từ chối (400) khi số bản ghi
  // KHỚP vượt ngưỡng ~20000, không đòi bắt buộc phải có bộ lọc. Bộ lọc ngày bên
  // dưới giữ số bản ghi ở mức thấp hơn ngưỡng đó rất nhiều.
  const queryParams = {
    page: params?.page ?? 1,
    page_size: params?.page_size ?? 10,
    ordering: params?.ordering ?? '-requested_at',
    provider: params?.provider,
    payment_status: params?.payment_status,
    order: params?.order,
    store_id: params?.store_id,
    requested_at__gte: params?.requested_at__gte,
    requested_at__lte: params?.requested_at__lte,
    latest_per_order: true,
  };

  return useQuery({
    queryKey: queryKeys.reports.dashboardTransactions(queryParams as Record<string, unknown>),
    queryFn: () => api.client.get<PaginatedLike<RecentTransactionItem>>('/payments/', queryParams),
    enabled: options?.enabled !== false,
  });
}

export function useDashboardOverview(
  params?: { store_id?: UUID },
  options?: { enabled?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.reports.dashboardOverview((params ?? {}) as Record<string, unknown>),
    queryFn: () =>
      api.client.get<DashboardOverviewResponse>('/reports/dashboard/', {
        store_id: params?.store_id,
      }),
    enabled: options?.enabled !== false,
  });
}

export interface CustomerSummaryParams {
  start_date?: string;
  end_date?: string;
  store_id?: UUID;
}

export interface CustomerSummaryResponse {
  total_customers?: number;
  first_time_customers?: number;
  returning_customers?: number;
  first_time_change_pct?: number | null;
  returning_change_pct?: number | null;
  new_customers_pct?: number | null;
}

/**
 * Customer summary metrics for the dashboard (GET /reports/customer-summary/).
 * Powers the "Customer" card + "Customers Overview" (First Time / Return).
 */
export function useCustomerSummary(
  params: CustomerSummaryParams = {},
  options?: { enabled?: boolean; keepPreviousData?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.reports.customerSummary(params as Record<string, unknown>),
    queryFn: () =>
      api.client.get<CustomerSummaryResponse>('/reports/customer-summary/', {
        start_date: params.start_date,
        end_date: params.end_date,
        store_id: params.store_id,
      }),
    enabled: options?.enabled !== false,
    // Đổi khoảng ngày -> queryKey đổi -> react-query coi là query MỚI và bật lại
    // `isLoading`, panel nháy skeleton. Với `keepPreviousData` thì dữ liệu kỳ cũ
    // ở nguyên trên màn hình cho tới khi có dữ liệu mới (chỉ `isFetching` +
    // `isPlaceholderData` bật). Mặc định TẮT để không đổi hành vi caller khác.
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
}

export function useOrderStatusTrend(
  params: RevenueTrendParams,
  options?: { enabled?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.reports.orderStatusTrend(
      params as unknown as Record<string, unknown>
    ),
    queryFn: () =>
      api.client.get<OrderStatusTrendResponse>('/reports/order-status-trend/', {
        start_date: params.start_date,
        end_date: params.end_date,
        period: params.period ?? 'daily',
        store_id: params.store_id,
      }),
    enabled:
      options?.enabled !== false &&
      Boolean(params.start_date) &&
      Boolean(params.end_date),
  });
}

export interface TopProductItem {
  product_id: string;
  product_name: string;
  product_sku?: string;
  quantity_sold: number;
  revenue: string;
  order_count?: number;
  cost?: string;
  profit?: string;
}

/**
 * Top sản phẩm bán chạy theo đúng khoảng ngày đang chọn.
 * Thay cho khối `top_products` của /reports/dashboard/ — khối đó là snapshot
 * không nhận date range, và group theo tên nên 2 sản phẩm trùng tên bị gộp.
 */
export function useTopProducts(
  params: { start_date: string; end_date: string; store_id?: UUID; limit?: number },
  options?: { enabled?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: [...queryKeys.reports.all, 'top-products', params] as const,
    queryFn: () =>
      api.client.get<{ data?: TopProductItem[]; results?: TopProductItem[] }>(
        '/reports/top-products/',
        {
          start_date: params.start_date,
          end_date: params.end_date,
          store_id: params.store_id,
          limit: params.limit ?? 10,
        }
      ),
    enabled:
      options?.enabled !== false &&
      Boolean(params.start_date) &&
      Boolean(params.end_date),
  });
}

export interface LostItemReportRow {
  product_id: string;
  product_name: string;
  product_sku?: string;
  /** Tổng số lượng thất thoát trong khoảng ngày; hàng cân ký có thể lẻ (vd 0.65). */
  quantity_lost: number;
  /** Số vụ ghi nhận (số phiên ABANDONED chứa sản phẩm này). */
  incident_count: number;
  /** Giá trị thất thoát theo giá bán, chuỗi decimal như các report khác. */
  lost_value: string;
  /** Mã tiền tệ ISO cho lost_value (BE trả kèm); thiếu thì UI fallback USD. */
  currency?: string | null;
  last_incident_at?: string | null;
}

/**
 * Top sản phẩm thất thoát ("top stolen items").
 *
 * GET /reports/lost-items/ — v1 chỉ tính items của session ABANDONED (khách rời
 * cửa hàng không thanh toán); lọc ngày theo thời điểm session bị đánh ABANDONED,
 * không theo lúc tạo giỏ. Inventory movement LOSS chưa tồn tại phía BE — gộp ở v2,
 * cùng shape nên FE không phải đổi. Endpoint hỗ trợ ?export=xlsx|pdf nếu sau này
 * cần nút export riêng cho bảng.
 * Env chưa deploy endpoint -> query lỗi -> bảng hiển thị trống, không vỡ trang.
 */
export function useTopLostItems(
  params: { start_date: string; end_date: string; store_id?: UUID; limit?: number },
  options?: { enabled?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: [...queryKeys.reports.all, 'top-lost-items', params] as const,
    queryFn: () =>
      api.client.get<{ data?: LostItemReportRow[]; results?: LostItemReportRow[] }>(
        '/reports/lost-items/',
        {
          start_date: params.start_date,
          end_date: params.end_date,
          store_id: params.store_id,
          limit: params.limit ?? 10,
        }
      ),
    enabled:
      options?.enabled !== false &&
      Boolean(params.start_date) &&
      Boolean(params.end_date),
    // Env chưa deploy endpoint -> 404; đừng retry dồn dập
    retry: false,
  });
}

/**
 * Chuỗi decimal của BE -> số, hoặc `null` khi field không có / không parse được.
 * Giữ được phân biệt "không có dữ liệu" với "bằng 0" — bảng cần đúng phân biệt
 * này để chọn giữa hiển thị "—" và hiển thị "0".
 */
function parseMoneyOrNull(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface StoreRevenueSummary {
  storeId: string;
  storeName: string;
  revenue: number;
  orderCount: number;
  /**
   * Bug #80: giá vốn và lợi nhuận của cửa hàng.
   *
   * `null` = response không có số (BE cũ chưa trả `totals.cost`/`totals.profit`)
   * -> bảng hiện "—". KHÁC với `0`, nghĩa là có số và số đó bằng không.
   */
  cost: number | null;
  profit: number | null;
  isLoading: boolean;
}

/**
 * Doanh thu + số đơn theo từng store, dùng cho báo cáo "theo địa điểm".
 * BE chưa có endpoint group-by-store nên gọi /reports/revenue/ cho từng store.
 */
export function useRevenueByStores(
  stores: Array<{ id: string; name: string }>,
  params: { start_date: string; end_date: string }
) {
  const api = useApi();

  return useQueries({
    queries: stores.map((store) => ({
      queryKey: [...queryKeys.reports.all, 'revenue-by-store', store.id, params] as const,
      queryFn: () =>
        api.client.get<RevenueTrendResponse>('/reports/revenue/', {
          start_date: params.start_date,
          end_date: params.end_date,
          store_id: store.id,
        }),
      enabled: Boolean(params.start_date) && Boolean(params.end_date),
    })),
    combine: (results): { data: StoreRevenueSummary[]; isLoading: boolean } => ({
      isLoading: results.some((result) => result.isLoading),
      data: stores.map((store, index) => ({
        storeId: store.id,
        storeName: store.name,
        revenue: Number.parseFloat(results[index]?.data?.totals?.revenue ?? '0') || 0,
        orderCount: results[index]?.data?.totals?.order_count ?? 0,
        // Bug #80: hai dòng này trước đây bị BỎ. Mỗi request per-store đã trả
        // `totals.cost` và `totals.profit` ngay cạnh `totals.revenue`, nhưng
        // bước `combine` chỉ nhặt revenue + order_count rồi vứt phần còn lại —
        // nên bảng "Profit by Location" có Transactions và Revenue mà Profit,
        // Cost Price luôn là "—", kể cả khi thẻ KPI Cost/Profit phía trên (đọc
        // cùng response) đang hiện số.
        cost: parseMoneyOrNull(results[index]?.data?.totals?.cost),
        profit: parseMoneyOrNull(results[index]?.data?.totals?.profit),
        isLoading: results[index]?.isLoading ?? false,
      })),
    }),
  });
}

export function useTopProductsByCategory(
  params: RevenueTrendParams & { limit_per_category?: number },
  options?: { enabled?: boolean; keepPreviousData?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.reports.topProductsByCategory(
      params as unknown as Record<string, unknown>
    ),
    queryFn: () =>
      api.client.get<TopProductsByCategoryResponse>('/reports/top-products-by-category/', {
        start_date: params.start_date,
        end_date: params.end_date,
        period: params.period ?? 'daily',
        store_id: params.store_id,
        limit_per_category: params.limit_per_category ?? 5,
      }),
    enabled:
      options?.enabled !== false &&
      Boolean(params.start_date) &&
      Boolean(params.end_date),
    // Xem ghi chú ở useCustomerSummary: giữ dữ liệu kỳ cũ trong lúc tải kỳ mới
    // để panel không nháy skeleton. Mặc định TẮT.
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
}
